import { useMutation } from "@apollo/client";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import { ProcessingStrategy } from "generated-graphql/graphql";
import { useCallback } from "react";

import { useCurrentUser } from "./useCurrentUser";
import { useFeatureFlags, useStoredFeatureFlags } from "./useFeatureFlags";

const QUEUE_DOCUMENT_AI_ENRICHMENT_EXISTING = gql(`
  mutation QueueDocumentAIEnrichmentForManifest($input: QueueDocumentAIEnrichmentJobExistingInput!) {
    queueDocumentAIEnrichmentJobExisting(input: $input) {
      id
      status
    }
  }
`);

/**
 * Queue manifest document extraction via the unified Document AI enrichment pipeline.
 * The pipeline detects RCRA manifests and automatically runs manifest-specific extraction.
 *
 * Returns an array of { job, documentId } for status tracking in the upload dialog.
 */
export function useQueueManifestDocumentExtraction() {
  const { isStaff } = useCurrentUser();
  // Gate on the *stored* waste-module value (not the staff-overridden view): the
  // backend pipeline filters documents on the real `waste-module` TenantConfig row,
  // so queueing on a tenant without it would mark the doc PENDING and then no-op.
  const { featureFlags: storedFlags } = useStoredFeatureFlags();
  // Customers can queue manifest extraction on manifest-views-v2 tenants (a
  // human-initiated, per-document action — same cost model as staff uploads);
  // staff resolve the flag on via its staff override.
  const { featureFlags } = useFeatureFlags();
  const [mutate, { loading }] = useMutation(QUEUE_DOCUMENT_AI_ENRICHMENT_EXISTING);

  const canQueueManifestExtraction =
    !!storedFlags[FeatureFlagKey.WasteModule] && !!featureFlags[FeatureFlagKey.ManifestViewsV2];

  const queueManifestDocumentExtraction = useCallback(
    async (input: {
      tenantId: string;
      documentIds: string[];
      facilityId?: string;
      manifestTrackingNumber?: string;
      generatorId?: string;
    }) => {
      // The backend rejects manifestOverrides from non-staff users, so customer
      // uploads always go through unaided auto-matching even when the caller has
      // facility/tracking context available.
      const includeOverrides =
        isStaff && (input.facilityId || input.manifestTrackingNumber || input.generatorId);
      const { data } = await mutate({
        variables: {
          input: {
            tenantId: input.tenantId,
            documentIds: input.documentIds,
            strategy: ProcessingStrategy.ExtractAllEmbedAll,
            manifestOverrides: includeOverrides
              ? input.documentIds.map((documentId) => ({
                  documentId,
                  facilityId: input.facilityId,
                  manifestTrackingNumber: input.manifestTrackingNumber,
                  generatorId: input.generatorId,
                }))
              : undefined,
          },
        },
      });

      const job = data?.queueDocumentAIEnrichmentJobExisting;
      if (!job) return [];

      // Return one entry per document, all pointing to the same enrichment job
      return input.documentIds.map((documentId) => ({
        documentId,
        job: { id: job.id, status: job.status },
      }));
    },
    [mutate, isStaff]
  );

  return { queueManifestDocumentExtraction, loading, canQueueManifestExtraction };
}
